import { BackButton } from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import { Alert, Box, Button, Divider, Stack, Typography } from "@mui/material";
import { memo } from "react";
import { useTranslation } from "react-i18next";

import ExecutionProgress from "../../../../../components/Dashboards/Executions/ExecutionProgress";
import ExecutionStatusChip from "../../../../../components/Dashboards/Executions/ExecutionStatusChip";
import { formatExecutionDateTime } from "../../../../../components/Dashboards/Executions/utils";
import { IDashboardGenerateExecution } from "../../../../../models/dashboard-generate-execution";

const translationPath = "page.dashboardExecutions.";

interface IExecutionDetailHeaderProps {
  execution: IDashboardGenerateExecution;
  onReprocess?: () => void;
}

const Info = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack spacing={0.25}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

const ExecutionDetailHeader = ({
  execution,
  onReprocess,
}: IExecutionDetailHeaderProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  return (
    <Box>
      <BackButton />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography variant="h5">
            {t(translationPath + "detail.header_title", {
              code: execution.code,
            })}
          </Typography>
          <ExecutionStatusChip status={execution.status} />
        </Stack>
        {onReprocess && (
          <Button
            variant="contained"
            size="small"
            startIcon={<ReplayIcon />}
            onClick={onReprocess}
          >
            {t(translationPath + "detail.reprocess_all")}
          </Button>
        )}
      </Stack>
      <Typography variant="subtitle1" color="text.secondary" mb={2}>
        {execution.dashboardPanel?.name ?? "-"} ·{" "}
        {execution.originGenerateDashboard?.name ?? "-"}
      </Typography>

      <Stack
        direction="row"
        flexWrap="wrap"
        gap={3}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Info
          label={t(translationPath + "created")}
          value={formatExecutionDateTime(execution.created, language)}
        />
        <Info
          label={t(translationPath + "finished_date")}
          value={formatExecutionDateTime(execution.finishedDate, language)}
        />
        <Info
          label={t(translationPath + "running_generates")}
          value={execution.runningGenerates}
        />
        <Info
          label={t(translationPath + "failed_generates")}
          value={execution.failedGenerates}
        />
      </Stack>

      <Box mt={2} maxWidth={480}>
        <Typography variant="caption" color="text.secondary">
          {t(translationPath + "progress")}
        </Typography>
        <ExecutionProgress execution={execution} />
      </Box>

      {execution.status === "failed" && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t(translationPath + "failed_alert")}
        </Alert>
      )}
      {execution.generatesPurgedDate && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t(translationPath + "purged_alert", {
            date: formatExecutionDateTime(
              execution.generatesPurgedDate,
              language,
            ),
          })}
        </Alert>
      )}
    </Box>
  );
};

export default memo(ExecutionDetailHeader);
